import { useState } from 'react';
import {
  Pressable,
  type PressableProps,
  type StyleProp,
  View,
  type ViewStyle,
} from 'react-native';
import { StyleSheet } from 'react-native-unistyles';

type SwitchVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'info'
  | 'error';
type SwitchSize = 'sm' | 'md' | 'lg';

interface SwitchProps
  extends Omit<
    PressableProps,
    | 'accessibilityRole'
    | 'accessibilityState'
    | 'children'
    | 'disabled'
    | 'style'
  > {
  defaultValue?: boolean;
  disabled?: boolean;
  onValueChange?: (value: boolean) => void;
  size?: SwitchSize;
  style?: StyleProp<ViewStyle>;
  value?: boolean;
  variant?: SwitchVariant;
}

export default function Switch({
  defaultValue = false,
  disabled,
  onValueChange,
  size = 'md',
  style: extraStyles,
  value,
  variant = 'primary',
  ...props
}: SwitchProps) {
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
  const checked = value ?? uncontrolledValue;

  styles.useVariants({
    checked,
    disabled,
    size,
    variant,
  });

  const onPress = () => {
    if (disabled) {
      return;
    }

    const nextValue = !checked;

    if (value === undefined) {
      setUncontrolledValue(nextValue);
    }

    onValueChange?.(nextValue);
  };

  return (
    <Pressable
      {...props}
      role="switch"
      aria-checked={checked}
      aria-disabled={disabled}
      accessibilityState={{ checked, disabled }}
      disabled={disabled}
      onPress={onPress}
      style={[styles.track, extraStyles]}
    >
      <View pointerEvents="none" style={styles.thumb} />
    </Pressable>
  );
}

const styles = StyleSheet.create((theme) => ({
  thumb: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.full,
    variants: {
      checked: {
        false: {},
        true: {},
      },
      disabled: {
        true: {},
      },
      size: {
        lg: {
          height: theme.size(7),
          width: theme.size(7),
        },
        md: {
          height: theme.size(5),
          width: theme.size(5),
        },
        sm: {
          height: theme.size(4),
          width: theme.size(4),
        },
      },
      variant: {
        error: {},
        info: {},
        primary: {},
        secondary: {},
        success: {},
        warning: {},
      },
    },
    compoundVariants: [
      {
        checked: true,
        size: 'lg',
        styles: {
          transform: [{ translateX: theme.size(5.5) }],
        },
      },
      {
        checked: true,
        size: 'md',
        styles: {
          transform: [{ translateX: theme.size(5) }],
        },
      },
      {
        checked: true,
        size: 'sm',
        styles: {
          transform: [{ translateX: theme.size(3.5) }],
        },
      },
    ],
  },
  track: {
    backgroundColor: theme.colors.surfaceMuted,
    borderRadius: theme.borderRadius.full,
    justifyContent: 'center',
    padding: theme.size(0.5),
    variants: {
      checked: {
        false: {},
        true: {},
      },
      disabled: {
        true: {
          opacity: 0.5,
        },
      },
      size: {
        lg: {
          height: theme.size(8),
          width: theme.size(15),
        },
        md: {
          height: theme.size(6),
          width: theme.size(11),
        },
        sm: {
          height: theme.size(5),
          width: theme.size(8.5),
        },
      },
      variant: {
        error: {},
        info: {},
        primary: {},
        secondary: {},
        success: {},
        warning: {},
      },
    },
    compoundVariants: [
      {
        checked: true,
        variant: 'error',
        styles: {
          backgroundColor: theme.colors.error,
        },
      },
      {
        checked: true,
        variant: 'info',
        styles: {
          backgroundColor: theme.colors.info,
        },
      },
      {
        checked: true,
        variant: 'primary',
        styles: {
          backgroundColor: theme.colors.primary,
        },
      },
      {
        checked: true,
        variant: 'secondary',
        styles: {
          backgroundColor: theme.colors.secondary,
        },
      },
      {
        checked: true,
        variant: 'success',
        styles: {
          backgroundColor: theme.colors.success,
        },
      },
      {
        checked: true,
        variant: 'warning',
        styles: {
          backgroundColor: theme.colors.warning,
        },
      },
    ],
  },
}));
